import { CSSProperties, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { useApiData } from 'lib/api';
import {
  getDefaultEmailTemplate,
  renderPlaceholdersInTemplates
} from 'shared-helpers/email';
import { ModalButtonWithForm } from 'components/common';
import { useBoxContext } from 'components/common/box';
import { EmailMessageEditor } from 'components/common/form';
import { UsersListSelectedUsers } from '.';

import type { FormApi } from 'informed';
import type { GetEmailTemplatesByTypeRes } from 'shared-types/responses';
import type { UserSelectionParams } from 'lib/users/getSelectedUsers';
import type { ButtonSize } from 'components/common/Button';
import type { SelectedUser } from './UsersListSelectedUsers';

interface Props {
  buttonIsDropdown?: boolean;
  buttonSize?: ButtonSize;
  emailButtonTarget?: string;
  id: string;
  includeGuestUsersWithEmail?: boolean;
  style?: CSSProperties;
  supervisorRouteSupervisorId?: number;
  userSelectionParams?: UserSelectionParams;
}

interface FormValues {
  email: {
    buttonEnabled: boolean;
    buttonLink: string;
    buttonText: string;
    content: string;
    contentAfterButton: string;
    footnote: string;
    heading: string;
    subject: string;
  };
}

const UsersSendMessage = ({
  buttonIsDropdown,
  buttonSize,
  emailButtonTarget,
  id,
  includeGuestUsersWithEmail,
  style,
  supervisorRouteSupervisorId,
  userSelectionParams
}: Props) => {
  const { selectedItems } = useBoxContext();

  const { dispatch } = useDispatch();

  const formApi = useRef<FormApi<FormValues>>(undefined);

  const router = useRouter();

  const {
    language,
    messages,
    pageState: { courseAccess, search },
    user: loggedInUser
  } = useReduxState();

  const [users, setUsers] = useState<SelectedUser[]>([]);
  const [visible, setVisible] = useState(false);

  const { data: emailTemplatesData } = useApiData<GetEmailTemplatesByTypeRes>({
    url: `/admin/email-templates-data?type=custom-email`
  });

  let emailTemplates = emailTemplatesData?.emailTemplates || [];

  emailTemplates = renderPlaceholdersInTemplates({
    emailTemplates,
    overridePlaceholderValues: { contactEmail: loggedInUser?.email }
  });

  const defaultEmailTemplate = getDefaultEmailTemplate({
    language,
    emailTemplates
  });

  const handleSubmit = async (values: FormValues) => {
    try {
      const response = await api.post(`/admin/emails/message`, {
        buttonEnabled: values.email.buttonEnabled,
        buttonLink: values.email.buttonLink,
        buttonText: values.email.buttonText,
        content: values.email.content,
        contentAfterButton: values.email.contentAfterButton,
        emailAddresses: users.map((currentUser) => currentUser.identifier),
        footnote: values.email.footnote,
        heading: values.email.heading,
        subject: values.email.subject,
        userIdsByEmail: users.reduce((idsByEmail, currentUser) => {
          idsByEmail[currentUser.identifier] = currentUser.id;
          return idsByEmail;
        }, {})
      });

      if (!response.data || !response.data.success) throw new Error();

      let notiticationMessage =
        response.data.successfulSends + ` ${messages.usersSendMessageSuccess}`;

      let notificationType: 'success' | 'warning' = 'success';
      if (response.data.erroredEmailAddresses.length) {
        notificationType = 'warning';

        notiticationMessage += `<br /><br />${messages.usersSendMessageError}`;

        response.data.erroredEmailAddresses.forEach((emailAddress: string) => {
          notiticationMessage += `<br />${emailAddress}`;
        });
      }

      formApi.current.reset();

      dispatch.addNotice({
        text: notiticationMessage,
        type: notificationType
      });
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  return !defaultEmailTemplate ? null : (
    <ModalButtonWithForm
      apiRef={formApi}
      buttonIcon="paper-plane"
      buttonIsDropdown={buttonIsDropdown}
      buttonSize={buttonSize}
      buttonStyle={style}
      buttonText={messages.usersSendMessage}
      initialValues={{
        email: {
          ...defaultEmailTemplate.email,
          ...(emailButtonTarget ? { buttonLink: emailButtonTarget } : {}),
          selectedTemplateIdentifier: defaultEmailTemplate.identifier
        }
      }}
      modalActionText={messages.usersSendMessage}
      modalAutoFocus={false}
      modalTitle={messages.usersSendMessage}
      modalWidth="wide"
      onHide={() => setVisible(false)}
      onShow={() => setVisible(true)}
      onSubmit={handleSubmit}
      submitDisabled={!users.length}
    >
      {() => (
        <>
          {visible ? (
            <UsersListSelectedUsers
              allowGuestUsers={includeGuestUsersWithEmail}
              onLoadedUsers={(users) => setUsers(users)}
              supervisorRouteSupervisorId={supervisorRouteSupervisorId}
              userSelectionParams={
                userSelectionParams
                  ? userSelectionParams
                  : {
                      courseAccess,
                      excludeInvalidAccounts: true,
                      excludeUsersWithoutEmail: true,
                      search,
                      selectedUserIds: Object.keys(selectedItems).map((id) =>
                        parseInt(id, 10)
                      ),
                      routerQueryParams: router.query,
                      userSelector: 'emails'
                    }
              }
            />
          ) : null}

          <EmailMessageEditor
            availableTemplates={emailTemplates}
            id={`send-message-${id}`}
            style={{ marginTop: 20 }}
          />
        </>
      )}
    </ModalButtonWithForm>
  );
};

export default UsersSendMessage;
